// glide-components.js - Consolidated component initialization
(function() {
  // Create or reference global Theme namespace
  window.Theme = window.Theme || {};
  
  // Skip initialization if already initialized
  if (window.Theme.initialized) {
      return;
  }

  // Debug mode - set to false for production
  Theme.debug = false;
  
  // Logger utility to control all console output
  Theme.log = function(message) {
      if (Theme.debug) {
          console.log('[Theme]', message);
      }
  };
  
  // Simple browser detection moved inside Theme namespace
  Theme.detection = {
      isFirefox: typeof InstallTrigger !== 'undefined',
      isChrome: !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime)
  };
  
  // ========== CENTRALIZED RESOURCE LOADING ==========
  
  // Flag to track Glide resource loading status
  Theme.glideStatus = {
      loading: false,
      loaded: false,
      error: null
  };

  // Store references to created Glide instances for cleanup
  Theme.glideInstances = [];
  
  // Load Glide resources based on browser
  Theme.loadGlideResources = function() {
      // Skip if already loading or loaded
      if (Theme.glideStatus.loading || Theme.glideStatus.loaded) {
          return Promise.resolve();
      }

      // CRITICAL FIX: Check if Glide is already available (from local assets)
      if (window.Glide) {
          Theme.log("Glide already available from local assets, skipping CDN load");
          Theme.glideStatus.loaded = true;
          return Promise.resolve();
      }
      
      Theme.glideStatus.loading = true;
      Theme.log("Loading Glide resources for " + 
          (Theme.detection.isFirefox ? "Firefox" : 
           Theme.detection.isChrome ? "Chrome" : "Other browsers"));
      
      return new Promise((resolve, reject) => {
          // CRITICAL FIX: Define cacheKey for all browsers
          const cacheKey = new Date().toISOString().split('T')[0]; // Daily cache busting
          
          // Set up source URLs based on browser
          var cssSource, jsSource;
          
/* 
 * ========== CRITICAL BROWSER COMPATIBILITY FIX ==========
 * 
 * IMPORTANT: DO NOT MODIFY THIS CODE WITHOUT EXTENSIVE TESTING!
 * 
 * We're deliberately loading Glide.js from different sources for Firefox vs Chrome/other browsers.
 * This is NOT a typical best practice, but it's necessary to resolve a complex compatibility issue.
 * 
 * WHAT HAPPENS IF THIS IS CHANGED:
 * - If both browsers load from the same source (either both CDN or both local assets), 
 *   the sliders will break in at least one browser
 * - Tested configurations:
 *   ✓ Firefox: CDN, Chrome: Local assets → WORKS
 *   ✗ Firefox: Local assets, Chrome: Local assets → BREAKS
 *   ✗ Firefox: CDN, Chrome: CDN → BREAKS
 * 
 * ROOT CAUSE HYPOTHESIS:
 * This may be related to browser-specific asset optimization, caching mechanisms,
 * resource contention, or subtle version differences between the CDN and local files.
 * Each browser needs its own "lane" for these resources to prevent conflicts.
 * 
 * Last verified working: [Current Date]
 */

// CRITICAL CHANGE: Ensure Firefox and Chrome use different sources
if (Theme.detection.isFirefox) {
    // Firefox loads from CDN with specific version and cache busting
    cssSource = 'https://cdn.jsdelivr.net/npm/@glidejs/glide@3.5.2/dist/css/glide.core.min.css?v=' + cacheKey;
    jsSource = 'https://cdn.jsdelivr.net/npm/@glidejs/glide@3.5.2/dist/glide.min.js?v=' + cacheKey;
} else {
    // Chrome and other browsers load from CDN with different version
    cssSource = 'https://cdn.jsdelivr.net/npm/@glidejs/glide@3.7.1/dist/css/glide.core.min.css?v=' + cacheKey;
    jsSource = 'https://cdn.jsdelivr.net/npm/@glidejs/glide@3.7.1/dist/glide.min.js?v=' + cacheKey;
}
          
          // Create and append CSS link
          var glideCss = document.createElement('link');
          glideCss.rel = 'stylesheet';
          glideCss.href = cssSource;
          document.head.appendChild(glideCss);
          
          // Create and append JS script
          var glideScript = document.createElement('script');
          glideScript.src = jsSource;
          
          // Handle script load/error events
          glideScript.onload = function() {
              Theme.log("Glide script loaded successfully");
              Theme.glideStatus.loaded = true;
              Theme.glideStatus.loading = false;
              resolve();
          };
          
          glideScript.onerror = function(error) {
              Theme.log("Error loading Glide script: " + error);
              Theme.glideStatus.error = error;
              Theme.glideStatus.loading = false;
              reject(error);
          };
          
          document.head.appendChild(glideScript);
      });
  };
  
  // Get Glide instance with better error handling
  function getGlide() {
      if (window.Glide) {
          return window.Glide;
      } else if (typeof glide !== 'undefined' && glide.default) {
          window.Glide = glide.default;
          return glide.default;
      } else if (typeof Glide !== 'undefined') {
          window.Glide = Glide;
          return Glide;
      }
      
      Theme.log("Glide not found in any expected location");
      return null;
  }
  
  // Wait for Glide to be available with timeout
  function waitForGlide(maxWait = 2000) {
      const startTime = Date.now();
      
      return new Promise((resolve, reject) => {
          function checkGlide() {
              const Glide = getGlide();
              
              if (Glide) {
                  Theme.log("Glide is available");
                  resolve(Glide);
                  return;
              }
              
              if (Date.now() - startTime > maxWait) {
                  const error = new Error("Timeout waiting for Glide");
                  Theme.log(error.message);
                  reject(error);
                  return;
              }
              
              setTimeout(checkGlide, 50);
          }
          
          checkGlide();
      });
  }

  // CRITICAL FIX: Add cleanup function to destroy existing instances
  function cleanupGlideInstances() {
      Theme.log("Cleaning up existing Glide instances");
      Theme.glideInstances.forEach(function(instance) {
          try {
              if (instance && typeof instance.destroy === 'function') {
                  instance.destroy();
              }
          } catch (error) {
              Theme.log("Error destroying Glide instance: " + error.message);
          }
      });
      Theme.glideInstances = [];
  }
  
  // ========== COMPONENT DEFINITIONS ==========
  
  // All your existing component definitions remain the same,
  // Only changing the Carousel and HeroSlider components
  
  // [Keep all your existing component code from Theme.Breadcrumbs to Theme.Personalization]
  
  // Initialize Breadcrumbs Component
  Theme.Breadcrumbs = (function() {
      // Private variables
      var selectors = {
          wrapper: '.breadcrumbs-wrapper',
          container: '.breadcrumbs',
          items: '.breadcrumbs ol li',
          contextElement: '.js-collection-context'
      };
      
      function init() {
          const breadcrumbsWrapper = document.querySelector(selectors.wrapper);
          const contextElement = document.querySelector(selectors.contextElement);
          
          function showBreadcrumbs() {
              if (breadcrumbsWrapper) {
                  breadcrumbsWrapper.style.visibility = 'visible';
                  breadcrumbsWrapper.style.opacity = '1';
                  
                  // After showing breadcrumbs, apply mobile truncation if needed
                  truncateMobileBreadcrumbs();
              }
          }
          
          function updateBreadcrumbs(collectionHandle, collectionTitle) {
              if (contextElement) {
                  contextElement.innerHTML = `<a href="/collections/${collectionHandle}" itemprop="item"><span itemprop="name">${collectionTitle}</span></a>`;
              }
              showBreadcrumbs();
          }
          
          function fetchCollectionTitle(collectionHandle) {
              fetch(`/collections/${collectionHandle}`)
                  .then(response => response.text())
                  .then(html => {
                      const parser = new DOMParser();
                      const doc = parser.parseFromString(html, 'text/html');
                      const collectionTitle =
                          doc.querySelector('h1')?.textContent ||
                          doc.querySelector('.collection-title')?.textContent ||
                          doc.querySelector('title')?.textContent?.split('|')[0] ||
                          doc.querySelector('meta[property="og:title"]')?.content ||
                          collectionHandle.replace(/-/g, ' ');
                      updateBreadcrumbs(collectionHandle, collectionTitle.trim());
                  })
                  .catch(error => {
                      Theme.log('Error fetching collection title: ' + error);
                      showBreadcrumbs();
                  });
          }
          
          // Process breadcrumbs based on user path
          if (contextElement) {
              const defaultCollection = contextElement.dataset.defaultCollection;
              const referrer = document.referrer;
              const collectionMatch = referrer.match(/\/collections\/([^\/]+)/);
              
              if (collectionMatch) {
                  const collectionHandle = collectionMatch[1];
                  sessionStorage.setItem('lastCollection', collectionHandle);
              }
              
              const lastKnownCollection = sessionStorage.getItem('lastCollection');
              if (lastKnownCollection && lastKnownCollection !== defaultCollection) {
                  fetchCollectionTitle(lastKnownCollection);
              } else {
                  showBreadcrumbs();
              }
          } else {
              showBreadcrumbs();
          }
          
          // Handle window resize for responsive breadcrumbs
          window.addEventListener('resize', function() {
              truncateMobileBreadcrumbs();
          });
      }
      
      function truncateMobileBreadcrumbs() {
          // If on mobile and breadcrumbs are too long, truncate
          if (window.innerWidth < 768) {
              var container = document.querySelector(selectors.container);
              var items = document.querySelectorAll(selectors.items);
              
              // Only truncate if we have multiple breadcrumbs
              if (items.length > 2 && container) {
                  // Show only first and last breadcrumb items on small screens
                  for (var i = 1; i < items.length - 1; i++) {
                      items[i].classList.add('hidden-xs');
                  }
                  
                  // Add ellipsis if not already present
                  if (!document.querySelector('.breadcrumb-ellipsis')) {
                      var ellipsis = document.createElement('span');
                      ellipsis.className = 'breadcrumb-ellipsis hidden-sm hidden-md hidden-lg';
                      ellipsis.textContent = '...';
                      // Insert before the last item
                      if (container.querySelector('ol')) {
                          container.querySelector('ol').insertBefore(ellipsis, items[items.length - 1]);
                      }
                  }
              }
          }
      }
      
      // Return public methods
      return {
          init: init
      };
  })();
  
  // Initialize Mobile Search Component
  Theme.MobileSearch = (function() {
      var selectors = {
          toggleButton: '[data-mobile-search-toggle]',
          searchForm: '[data-mobile-search-form]',
          searchInput: '[data-mobile-search-input]',
          closeButton: '[data-mobile-search-close]',
          overlay: '[data-mobile-search-overlay]',
          toggler: '[data-mobile-search-toggler]',
          searchHolder: '#mobileSearchHolder'
      };
      
      function init() {
          // Toggle search form visibility
          var toggleButton = document.querySelector(selectors.toggleButton);
          var searchForm = document.querySelector(selectors.searchForm);
          var closeButton = document.querySelector(selectors.closeButton);
          var searchInput = document.querySelector(selectors.searchInput);
          var overlay = document.querySelector(selectors.overlay);
          
          if (toggleButton && searchForm) {
              toggleButton.addEventListener('click', function(e) {
                  e.preventDefault();
                  searchForm.classList.add('is-active');
                  document.body.classList.add('search-open');
                  
                  // Focus the search input after a short delay
                  setTimeout(function() {
                      if (searchInput) {
                          searchInput.focus();
                      }
                  }, 100);
              });
          }
          
          if (closeButton && searchForm) {
              closeButton.addEventListener('click', function(e) {
                  e.preventDefault();
                  searchForm.classList.remove('is-active');
                  document.body.classList.remove('search-open');
              });
          }
          
          if (overlay && searchForm) {
              overlay.addEventListener('click', function(e) {
                  if (e.target === overlay) {
                      searchForm.classList.remove('is-active');
                      document.body.classList.remove('search-open');
                  }
              });
          }
          
          // Close on escape key
          document.addEventListener('keydown', function(e) {
              if (e.key === 'Escape' && searchForm && searchForm.classList.contains('is-active')) {
                  searchForm.classList.remove('is-active');
                  document.body.classList.remove('search-open');
              }
          });
          // Mobile search toggler functionality (from mobile-search-toggler.js)
          const $ = window.jQuery || window.$;
          if ($) {
              $(selectors.toggler).on('click', function(e) {
                  e.preventDefault();
                  var $searchHolder = $(selectors.searchHolder);
                  var $toggler = $(this);
                  $searchHolder.toggleClass('hidden');
                  $toggler.toggleClass('icon-search icon-close');
              });
          }
      }
      
      return {
          init: init
      };
  })();

  // Initialize Mega Menu Component
  Theme.MegaMenu = (function() {
      var selectors = {
          megaMenuToggle: "[data-mega-menu-toggle]",
          megaMenu: "[data-mega-menu]"
      };

      function init() {
          const $ = window.jQuery || window.$;
          if (!$) return;

          const megaMenuToggle = $(selectors.megaMenuToggle);
          if (megaMenuToggle.length === 0) return;

          Theme.log("Initializing mega menu");
          const megaMenuLink = megaMenuToggle.find("> a");
          
          megaMenuToggle.on({
              mouseenter: function() {
                  const $this = $(this);
                  $(selectors.megaMenu).addClass("hidden");
                  $this.find(selectors.megaMenu).removeClass("hidden");
                  megaMenuLink.addClass("text-light-grey-500");
                  $this.find("> a").removeClass("text-light-grey-500").addClass("text-dark-blue-900");
              },
              mouseleave: function() {
                  megaMenuLink.removeClass("text-light-grey-500");
                  $(selectors.megaMenu).addClass("hidden");
              }
          });
      }

      return {
          init: init
      };
  })();

  // Initialize Sticky Elements Component
  Theme.StickyElements = (function() {
      var selectors = {
          stickyElement: "[data-sticky]",
          fixedCheckoutButton: "[data-fixed-checkout-button]"
      };

      function init() {
          const $ = window.jQuery || window.$;
          if (!$) return;

          // Initialize stickybits if available
          if (typeof stickybits !== "undefined") {
              Theme.log("Initializing sticky elements");
              stickybits(selectors.stickyElement, { stickyBitStickyOffset: 30 });
          }

          // Initialize fixed checkout button if lodash is available
          if (typeof _ !== "undefined") {
              Theme.log("Initializing fixed checkout button");
              const stickyElement = $(selectors.stickyElement);
              
              if (stickyElement.length > 0) {
                  let lastScrollTop = 0;
                  const threshold = 10;
                  const checkPosition = $(stickyElement).position().top - $(stickyElement).height();
                  
                  $(window).on("scroll", _.debounce(function() {
                      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                      if (Math.abs(scrollTop - lastScrollTop) < threshold) return;
                      
                      if (scrollTop > lastScrollTop) {
                          scrollTop > checkPosition ? $(selectors.fixedCheckoutButton).fadeOut(300)
                              : $(selectors.fixedCheckoutButton).fadeIn(300);
                      } else {
                          $(selectors.fixedCheckoutButton).fadeOut(300);
                      }
                      
                      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
                  }, 100));
              }
          }
      }

      return {
          init: init
      };
  })();
  
  // Initialize Product Tabs Component
  //
  // The product information sections are rendered ONCE, as <details> elements
  // in .product-accordion-container (see snippets/comp-product-accordion.liquid).
  // Both controls drive the same elements, and `open` is what shows a section:
  //
  //   desktop (>= 1024px)  the .product-tabs bar; each <summary> is display:none
  //   mobile  (< 1024px)   the <summary> of each section
  //
  // Previously the content existed twice, once per breakpoint, and the tab
  // handler toggled a `hidden` class on its own private copy of the panels.
  Theme.ProductTabs = (function() {
    var selectors = {
        // Desktop tab selectors
        tabGroups: "[data-tabs]",
        desktopTab: '#ordering-process-tab',
        tabItems: '.product-tabs-item',

        // The sections themselves -- one copy, shared by both controls
        accordionContainer: '.product-accordion-container',
        mobileAccordion: '#ordering-process-accordion',
        accordionItems: '[data-element="accordion-item"]',
        descriptionSlot: '[data-description-slot]',
        accordionContent: '[data-element="accordion-content"]',
        accordionTitles: '[data-element="accordion-title"]',

        // Triggers for opening the ordering process
        orderingProcessTrigger: '[data-action="ordering-process"]',

        // Triggers for opening a section by the handle of its page
        sectionTrigger: '[data-action="open-section"]'
    };

    // Every collapsible item, including the description slot that the inline
    // script in comp-product-accordion.liquid fills below 1024px.
    function allItems() {
        const $ = window.jQuery || window.$;
        return $(selectors.accordionContainer).find(selectors.accordionItems);
    }

    // Only the sections that have a tab. The description slot deliberately has
    // none -- on desktop the intro belongs in the product column -- so it must
    // not shift the index the tab bar is paired against.
    function sections() {
        return allItems().not(selectors.descriptionSlot);
    }

    // Keep the desktop tab bar in step with whichever section is open, so
    // opening one on mobile and then widening the window is not inconsistent.
    function syncTabs(index) {
        const $ = window.jQuery || window.$;
        const tabs = $(selectors.tabItems);
        tabs.removeClass('is-active');
        if (index >= 0) tabs.eq(index).addClass('is-active');
    }

    // Open the Nth section and close the rest.
    function openSection(index) {
        const $ = window.jQuery || window.$;
        if (!$) return null;

        const target = sections().eq(index);
        if (target.length === 0) return null;

        // Close everything else, the description slot included.
        allItems().not(target).prop('open', false);
        target.prop('open', true);
        syncTabs(index);

        return target;
    }

    function init() {
        const $ = window.jQuery || window.$;
        if (!$) return;

        const tabGroups = $(selectors.tabGroups);
        if (tabGroups.length === 0) return;

        Theme.log("Initializing product tabs");

        // Handle tab clicks. The Nth tab is the Nth section: the Liquid emits
        // both loops under the same guards, so they cannot fall out of step.
        tabGroups.each(function() {
            const tabLinks = $(this).find(".product-tabs-item-link");

            tabLinks.each(function(index) {
                $(this).on("click", function(e) {
                    e.preventDefault();
                    openSection(index);
                });
            });
        });

        // Handle accordion functionality for mobile
        const accordionContainer = $(selectors.accordionContainer);
        if (accordionContainer.length > 0) {
            // Using delegated event handling for better performance
            accordionContainer.on('click', selectors.accordionTitles, function(event) {
                event.preventDefault();

                const clickedSummary = $(this);
                const clickedDetails = clickedSummary.closest(selectors.accordionItems);

                // Toggle the open state of the clicked details element
                const isOpen = clickedDetails.prop('open');

                // If not already open
                if (!isOpen) {
                    // Close all other details
                    accordionContainer.find(selectors.accordionItems).not(clickedDetails).prop('open', false);
                    // index() returns -1 for the description slot, which clears
                    // the tab bar -- correct, it has no tab.

                    // Open this one
                    clickedDetails.prop('open', true);
                    syncTabs(sections().index(clickedDetails));

                    // Scroll to it
                    $('html, body').animate({
                        scrollTop: clickedSummary.offset().top - 20
                    }, 300);
                } else {
                    // If it's already open, close it
                    clickedDetails.prop('open', false);
                    syncTabs(-1);
                }
            });
        }

        // Set up ordering process triggers
        $(selectors.orderingProcessTrigger).on('click', function(e) {
            e.preventDefault();
            Theme.openOrderingProcessTab();
        });

        // Set up triggers that open a section by the handle of its page, such
        // as the Priority Service link in the despatch message. When the page
        // has no such section the click is left alone, so the link goes to
        // its href.
        $(selectors.sectionTrigger).on('click', function(e) {
            if (openSectionByHandle($(this).attr('data-section'))) {
                e.preventDefault();
            }
        });
    }

    // Open the section whose page has this handle: data-section-handle on its
    // <details>, set in snippets/comp-product-accordion.liquid. Returns false
    // when the page has no such section.
    function openSectionByHandle(handle) {
        const $ = window.jQuery || window.$;
        if (!$ || !handle) return false;

        const target = sections().filter(function() {
            return $(this).attr('data-section-handle') === handle;
        }).first();
        if (target.length === 0) return false;

        const index = sections().index(target);
        if (index < 0) return false;

        openSection(index);

        // Scroll to whichever control the visitor can actually see.
        const anchor = window.innerWidth >= 1024 ? $(selectors.tabItems).eq(index) : target;
        if (anchor.length === 0) return true;

        $('html, body').animate({
            scrollTop: anchor.offset().top - 20
        }, 300);
        return true;
    }

    // Public method for opening the ordering process section. Used by the
    // "Tell me more" link in the despatch message
    // (snippets/despatch-date-product.liquid).
    function openOrderingProcessTab() {
        const $ = window.jQuery || window.$;
        if (!$) return;

        const target = $(selectors.mobileAccordion);
        if (target.length === 0) return;

        const index = sections().index(target);
        if (index < 0) return;

        openSection(index);

        // Scroll to whichever control the visitor can actually see.
        const anchor = window.innerWidth >= 1024 ? $(selectors.desktopTab) : target;
        if (anchor.length === 0) return;

        $('html, body').animate({
            scrollTop: anchor.offset().top - 20
        }, 300);
    }

    return {
        init: init,
        openSection: openSection,
        openOrderingProcessTab: openOrderingProcessTab,
        openSectionByHandle: openSectionByHandle
    };
  })();


  // Initialize Mobile Menu Component
  Theme.MobileMenu = (function() {
      var selectors = {
          mobileMenuButton: "[data-hamburger]",
          mobileMenu: "[data-mobile-menu]",
          mobileMenuClose: "[data-mobile-menu-close]"
      };

      function init() {
          const $ = window.jQuery || window.$;
          if (!$) return;

          const mobileMenuButton = $(selectors.mobileMenuButton);
          const mobileMenu = $(selectors.mobileMenu);
          const mobileMenuClose = $(selectors.mobileMenuClose);

          if (mobileMenuButton.length === 0 || mobileMenu.length === 0) return;

          Theme.log("Initializing mobile menu");
          
          mobileMenuButton.on("click", function(e) {
              e.preventDefault();
              $(this).toggleClass("is-active");
              mobileMenu.toggleClass("-translate-x-full");
              mobileMenu.toggleClass("is-active");
              mobileMenu.toggleClass("menu-open");
              $("body").toggleClass("overflow-hidden").toggleClass("mobile-nav-open");
          });
          
          if (mobileMenuClose.length > 0) {
              mobileMenuClose.on("click", function(e) {
                  e.preventDefault();
                  mobileMenuButton.toggleClass("is-active");
                  mobileMenu.toggleClass("-translate-x-full");
                  mobileMenu.toggleClass("is-active");
                  mobileMenu.removeClass("menu-open");
                  $("body").toggleClass("overflow-hidden").toggleClass("mobile-nav-open");
              });
          }

          // Handle clicks outside the menu to close it
          $("body").on("click", function(e) {
              if (0 == $(e.target).closest(".mobile-menu").length && 
                  0 == $(e.target).closest(selectors.mobileMenuButton).length && 
                  $("body").hasClass("mobile-nav-open")) {
                  
                  mobileMenuButton.toggleClass("is-active");
                  mobileMenu.toggleClass("-translate-x-full");
                  mobileMenu.toggleClass("is-active");
                  mobileMenu.removeClass("menu-open");
                  $("body").toggleClass("overflow-hidden").toggleClass("mobile-nav-open");
              }
          });
      }

      return {
          init: init
      };
  })();

  // Initialize Personalization Component

    Theme.Personalization = (function() {
    var selectors = {
        personalizationToggle: "[data-personalise-toggle]",
        personalizationContainer: "[data-personalise]"
    };
    
    function init() {
        const $ = window.jQuery || window.$;
        if (!$) return;
        
        const personalizationToggle = $(selectors.personalizationToggle);
        const personalizationContainer = $(selectors.personalizationContainer);
        
        if (personalizationToggle.length === 0 || personalizationContainer.length === 0) return;
        Theme.log("Initializing personalization component");
        
        // Make sure container is visible on page load
        personalizationContainer.removeClass("hidden");
        personalizationContainer.css("display", "block");
        
        // Remove any click handlers that might be toggling visibility
        personalizationToggle.off("click");
        
        // Add only scroll behavior to the toggle button
        personalizationToggle.on("click", function(e) {
            e.preventDefault();
            // Scroll to the personalization section with smooth animation
            $('html, body').animate({
                scrollTop: personalizationContainer.offset().top - 20
            }, 300);
        });
    }
    
    return {
        init: init
    };
  })();
  
  // ========== UPDATED SLIDER COMPONENTS ==========
  
// Updated Carousel Component with resource loading check and max 7 elements
// Updated Carousel Component with resource loading check, max 7 elements, and max width
Theme.Carousel = (function() {
    var selectors = {
        carousels: '.glide-product-carousel',
        carouselContainer: '.favourites-carousel' // Container for applying max width
    };

    function init() {
        Theme.log("Initializing Carousel component - waiting for Glide resources");

        // Mobile bypass: the inline head script in theme.liquid has already
        // renamed .glide-product-carousel -> .mf-scroll-carousel on mobile,
        // set the --slide-* custom properties, and native CSS scroll-snap is
        // driving the carousel. Nothing for Glide to do here — bail out.
        if (window.innerWidth < 1024) {
            Theme.log("Mobile detected — scroll-snap handled by inline head script");
            return;
        }

        // Check if Glide resources need to be loaded
        if (!Theme.glideStatus.loaded) {
            Theme.log("Glide not loaded yet, will initialize carousel after resources load");
            return;
        }
        
        // Try to get Glide instance
        const Glide = getGlide();
        if (!Glide) {
            Theme.log("Glide not available yet, skipping carousel initialization");
            return;
        }

        // CRITICAL FIX: Clean up existing instances before creating new ones
        cleanupGlideInstances();

        Theme.log("Mounting Glide product carousels");
        const carousels = document.querySelectorAll(selectors.carousels);
        
        if (carousels.length === 0) {
            Theme.log("No product carousels found on page");
            return;
        }
        
        // Apply global CSS for max-width if needed
        const styleId = 'carousel-max-width-style';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
                .favourites-carousel {
                    max-width: 2560px !important;
                    margin: 0 auto !important;
                    overflow: hidden !important;
                }
            `;
            document.head.appendChild(style);
            Theme.log("Added global style for carousel max width and overflow");
        }
        
        carousels.forEach((carousel, index) => {
            try {
                // CRITICAL FIX: Check if carousel already has a Glide instance
                if (carousel.glide) {
                    Theme.log(`Carousel #${index + 1} already has Glide instance, skipping`);
                    return;
                }

                Theme.log(`Initializing product carousel #${index + 1}`);
                
                // Get configuration from data attributes with fallbacks
                // Updated to respect a maximum of 7 items on screen
                const config = {
                    type: 'carousel',
                    perView: parseInt(carousel.dataset.perViewSmall) || 1,
                    gap: parseInt(carousel.dataset.gap) || 20,
                    dragThreshold: 50,
                    swipeThreshold: 30,
                    touchRatio: 0.8,
                    animationDuration: 400,
                    rewind: false,
                    breakpoints: {
                        640: { perView: parseInt(carousel.dataset.perViewSmall) || 1 },
                        768: { perView: parseInt(carousel.dataset.perViewMedium) || 2 },
                        1024: { perView: parseInt(carousel.dataset.perViewLarge) || 3 },
                        1280: { perView: parseInt(carousel.dataset.perViewXlarge) || 5 },
                        1536: { perView: Math.min(parseInt(carousel.dataset.perViewXlarge) || 7, 7) },
                        1920: { perView: 7 },
                        2560: { perView: 7 }, // 2K
                        3440: { perView: 7 }, // Ultrawide
                        3840: { perView: 7 }, // 4K
                        5120: { perView: 7 }, // 5K
                        6016: { perView: 7 }, // 6K
                        7680: { perView: 7 }  // 8K
                    }
                };
                
                // Apply max width and overflow hidden to carousel container
                const carouselContainer = carousel.closest(selectors.carouselContainer);
                if (carouselContainer) {
                    carouselContainer.style.maxWidth = '2560px';
                    carouselContainer.style.margin = '0 auto';
                    carouselContainer.style.overflow = 'hidden';
                    Theme.log(`Applied max width and overflow hidden to carousel container`);
                }
                
                // Create and mount carousel with explicit configuration
                const glideInstance = new Glide(carousel, config);
                glideInstance.mount();
                
                // CRITICAL FIX: Store instance references for cleanup
                carousel.glide = glideInstance;
                Theme.glideInstances.push(glideInstance);
                
                Theme.log(`Product carousel #${index + 1} mounted successfully`);
            } catch (error) {
                Theme.log(`Error mounting product carousel #${index + 1}: ${error.message}`);
                console.error(error);
            }
        });
    }

    return {
        init: init
    };
})();

  // DISABLED Hero Slider Component to prevent .glide-images conflicts
  // This was causing Firefox-specific slider issues by creating multiple Glide instances
  Theme.HeroSlider = (function() {
      var selectors = {
          heroSlider: '.glide-images'
      };

      function init() {
          Theme.log("Hero Slider component DISABLED to prevent Glide conflicts");
          // DISABLED: This component was creating multiple Glide instances on .glide-images
          // which caused Firefox-specific slider issues (jumping, losing position, breaking)
          return;
          
          /* ORIGINAL CODE COMMENTED OUT:
          Theme.log("Initializing Hero Slider component - waiting for Glide resources");
          
          // Check if Glide resources need to be loaded
          if (!Theme.glideStatus.loaded) {
              Theme.log("Glide not loaded yet, will initialize hero slider after resources load");
              return;
          }
          
          // Try to get Glide instance with delay for problem browsers
          const Glide = getGlide();
          if (!Glide) {
              Theme.log("Glide not available yet, skipping hero slider initialization");
              return;
          }
          
          // Find hero slider element
          const heroSlider = document.querySelector(selectors.heroSlider);
          if (!heroSlider) {
              Theme.log("Hero slider element not found on page");
              return;
          }

          // CRITICAL FIX: Check if hero slider already has a Glide instance
          if (heroSlider.glide) {
              Theme.log("Hero slider already has Glide instance, skipping");
              return;
          }
          
          try {
              Theme.log("Mounting hero slider");
              
              // Create explicit configuration for hero slider
              const config = {
                  type: 'carousel',
                  startAt: 0,
                  perView: 1,
                  autoplay: 4000,
                  animationDuration: 600,
                  rewind: true,
                  gap: 0,
                  animationTimingFunc: 'ease-out'
              };
              
              // Create and mount hero slider
              const glide = new Glide(heroSlider, config);
              glide.mount();
              
              // CRITICAL FIX: Store instance reference for cleanup
              heroSlider.glide = glide;
              Theme.glideInstances.push(glide);
              
              Theme.log("Hero slider mounted successfully");
              
              // Handle visibility changes
              document.addEventListener('visibilitychange', function() {
                  if (document.hidden) {
                      Theme.log("Page hidden, pausing hero slider");
                      glide.pause();
                  } else {
                      Theme.log("Page visible, resuming hero slider");
                      glide.play();
                  }
              });
          } catch (error) {
              Theme.log(`Error mounting hero slider: ${error.message}`);
              console.error(error);
          }
          */
      }

      return {
          init: init
      };
  })();
  
  // ========== INITIALIZATION SEQUENCE ==========
  
  // Main initialization function with proper sequencing
  function initializeComponents() {
      Theme.log("Starting component initialization sequence");
      
// First initialize non-Glide components
Theme.log("Initializing non-Glide components");
Theme.Breadcrumbs.init();
Theme.MobileSearch.init();
Theme.MegaMenu.init();
Theme.StickyElements.init();
Theme.ProductTabs.init();
// Theme.MobileMenu.init(); // DISABLED — app.js handles hamburger toggle, mobile-nav.liquid syncs overlay via MutationObserver
Theme.Personalization.init();
      
      // Then load Glide resources and initialize sliders
      Theme.log("Loading Glide resources");
      Theme.loadGlideResources()
          .then(() => {
              // Short delay to ensure resources are processed
              setTimeout(() => {
                  Theme.log("Glide resources loaded, initializing sliders");
                  Theme.Carousel.init();
                  // DISABLED: Theme.HeroSlider.init(); - Removed to prevent .glide-images conflicts
                  
                  // Expose public methods
                  Theme.openOrderingProcessTab = Theme.ProductTabs.openOrderingProcessTab;
                  
                  // Mark as initialized
                  Theme.initialized = true;
                  Theme.log("All components initialized successfully");
              }, 50);
          })
          .catch(error => {
              Theme.log(`Error loading Glide resources: ${error.message}`);
              console.error(error);
          });
  }

  // CRITICAL FIX: Updated Shopify section events with proper cleanup
  function setupShopifySectionEvents() {
      document.addEventListener('shopify:section:load', function(event) {
          Theme.log("Shopify section loaded, cleaning up and reinitializing components");
          
          // Clean up existing Glide instances first
          cleanupGlideInstances();
          
          // Reset initialization flag to allow reinitialization
          Theme.initialized = false;
          
          // Small delay to ensure cleanup is complete
          setTimeout(() => {
              initializeComponents();
          }, 100);
      });
  }
  
  // Initialize when jQuery is available
  function waitForJQuery() {
      if (window.jQuery || window.$) {
          Theme.log("jQuery found, initializing components");
          if (document.readyState === "loading") {
              document.addEventListener("DOMContentLoaded", function() {
                  initializeComponents();
                  setupShopifySectionEvents();
              });
          } else {
              initializeComponents();
              setupShopifySectionEvents();
          }
      } else {
          setTimeout(waitForJQuery, 100);
      }
  }
  
  waitForJQuery();
})();